import {
  AbsenceRequestResponseDto,
  CreateAbsenceRequestDto,
  CreatePreApprovedAbsenceDto,
  DenyAbsenceRequestDto,
  RevokeAbsenceRequestDto,
  AbsenceRequestStatus,
  PreviewAbsenceRequestDto,
  AbsenceRequestPreviewDto,
} from "../types/absence";
import { PaginatedList, PaginationParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

export const absenceRequestsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get current user's absence requests
    getMyAbsenceRequests: builder.query<
      PaginatedList<AbsenceRequestResponseDto>,
      PaginationParams
    >({
      query: (params) => ({
        url: "absence-requests/me",
        params,
      }),
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get all absence requests (Admin)
    getAllAbsenceRequests: builder.query<
      PaginatedList<AbsenceRequestResponseDto>,
      { paginationParams: PaginationParams }
    >({
      query: ({ paginationParams }) => ({
        url: "absence-requests",
        params: paginationParams,
      }),
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get pending absence requests (Admin)
    getPendingAbsenceRequests: builder.query<
      PaginatedList<AbsenceRequestResponseDto>,
      PaginationParams
    >({
      query: (params) => ({
        url: "absence-requests/pending",
        params,
      }),
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get pending count (Admin)
    getPendingAbsenceRequestsCount: builder.query<number, void>({
      query: () => "absence-requests/pending/count",
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get calendar view data (Admin)
    getAbsenceRequestsForCalendar: builder.query<
      AbsenceRequestResponseDto[],
      { startDate: string; endDate: string; status?: AbsenceRequestStatus }
    >({
      query: (params) => ({
        url: "absence-requests/calendar",
        params,
      }),
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get single absence request
    getAbsenceRequestById: builder.query<AbsenceRequestResponseDto, number>({
      query: (id) => `absence-requests/${id}`,
      providesTags: (result, error, id) => [{ type: "AbsenceRequests", id }],
      transformResponse: unwrapApiResponse,
    }),

    // Get overlapping absence requests for same company (Admin)
    getOverlappingAbsenceRequests: builder.query<
      AbsenceRequestResponseDto[],
      number
    >({
      query: (requestId) => `absence-requests/${requestId}/overlapping`,
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Get employee's absence requests
    getEmployeeAbsenceRequests: builder.query<
      PaginatedList<AbsenceRequestResponseDto>,
      { employeeId: number; pagination: PaginationParams }
    >({
      query: ({ employeeId, pagination }) => ({
        url: `absence-requests/employee/${employeeId}`,
        params: pagination,
      }),
      providesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Create absence request
    createAbsenceRequest: builder.mutation<
      AbsenceRequestResponseDto,
      CreateAbsenceRequestDto
    >({
      query: (request) => ({
        url: "absence-requests",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["AbsenceRequests", "AbsenceBalances"],
      transformResponse: unwrapApiResponse,
    }),

    // Create pre-approved absence (Admin only)
    createPreApprovedAbsence: builder.mutation<
      AbsenceRequestResponseDto,
      CreatePreApprovedAbsenceDto
    >({
      query: (request) => ({
        url: "absence-requests/admin/create",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["AbsenceRequests", "AbsenceBalances"],
      transformResponse: unwrapApiResponse,
    }),

    // Preview absence request (Admin only)
    previewAbsenceRequest: builder.mutation<
      AbsenceRequestPreviewDto,
      PreviewAbsenceRequestDto
    >({
      query: (request) => ({
        url: "absence-requests/preview",
        method: "POST",
        body: request,
      }),
      transformResponse: unwrapApiResponse,
    }),

    // Approve absence request (Admin)
    approveAbsenceRequest: builder.mutation<AbsenceRequestResponseDto, number>({
      query: (id) => ({
        url: `absence-requests/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: ["AbsenceRequests", "AbsenceBalances"],
      transformResponse: unwrapApiResponse,
    }),

    // Deny absence request (Admin)
    denyAbsenceRequest: builder.mutation<
      AbsenceRequestResponseDto,
      { id: number; dto: DenyAbsenceRequestDto }
    >({
      query: ({ id, dto }) => ({
        url: `absence-requests/${id}/deny`,
        method: "POST",
        body: dto,
      }),
      invalidatesTags: ["AbsenceRequests"],
      transformResponse: unwrapApiResponse,
    }),

    // Cancel absence request
    cancelAbsenceRequest: builder.mutation<AbsenceRequestResponseDto, number>({
      query: (id) => ({
        url: `absence-requests/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["AbsenceRequests", "AbsenceBalances"],
      transformResponse: unwrapApiResponse,
    }),

    // Revoke absence request (Admin)
    revokeAbsenceRequest: builder.mutation<
      AbsenceRequestResponseDto,
      { id: number; dto: RevokeAbsenceRequestDto }
    >({
      query: ({ id, dto }) => ({
        url: `absence-requests/${id}/revoke`,
        method: "POST",
        body: dto,
      }),
      invalidatesTags: ["AbsenceRequests", "AbsenceBalances"],
      transformResponse: unwrapApiResponse,
    }),
  }),
});

export const {
  useGetMyAbsenceRequestsQuery,
  useGetAllAbsenceRequestsQuery,
  useGetPendingAbsenceRequestsQuery,
  useGetPendingAbsenceRequestsCountQuery,
  useGetAbsenceRequestsForCalendarQuery,
  useGetAbsenceRequestByIdQuery,
  useGetOverlappingAbsenceRequestsQuery,
  useGetEmployeeAbsenceRequestsQuery,
  useCreateAbsenceRequestMutation,
  useCreatePreApprovedAbsenceMutation,
  usePreviewAbsenceRequestMutation,
  useApproveAbsenceRequestMutation,
  useDenyAbsenceRequestMutation,
  useCancelAbsenceRequestMutation,
  useRevokeAbsenceRequestMutation,
} = absenceRequestsApi;







